"use client";

import { useId } from "react";
import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import { useOptionalTalentSearchResults } from "@/components/search/talent-search-results-provider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  CERTIFICATION_STATUS_OPTIONS,
  CERTIFICATION_STATUS_UNAVAILABLE,
} from "@/features/search/lib/certifications";
import type { SearchFiltersSchemaCertificationStatus } from "@/lib/api/model";
import type { AutocompleteResult } from "@/lib/api/types";
import { hasEntityForm } from "@/lib/search/entity-variants";
import { VariantsDropdown } from "../input/internal/variants-dropdown";
import {
  ClearChipsButton,
  FilterChip,
  FilterFieldContainer,
  FilterFieldContent,
  FilterFieldLabel,
} from "../shared";

type CertificationFilterKey = "certification_names" | "certification_vendors";

function CertificationChipField({
  filterKey,
  label,
}: {
  filterKey: CertificationFilterKey;
  label: string;
}) {
  const { filters, updateFilter } = useTalentSearchFilters();
  const items = filters[filterKey] ?? [];

  const add = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (hasEntityForm(items, trimmed)) return;
    updateFilter(filterKey, [...items, { text: trimmed, required: false }]);
  };

  return (
    <>
      <FilterFieldContainer>
        <FilterFieldLabel>{label}</FilterFieldLabel>
        <FilterFieldContent>
          <AutocompleteInput
            variant="filter"
            field={filterKey}
            placeholder="Search..."
            showAllOnOpen
            onSelect={(result: AutocompleteResult) => add(result.value)}
            onFreeText={add}
          />
        </FilterFieldContent>
      </FilterFieldContainer>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {items.map((item, i) => {
            const excluded = item.mode === "exclude";
            const required = item.required ?? false;
            return (
              <FilterChip
                key={i}
                label={item.text}
                variants={item.variants}
                beforeActions={
                  <VariantsDropdown
                    compact
                    field={filterKey}
                    item={item}
                    onChange={(updated) => {
                      const next = [...items];
                      next[i] = updated;
                      updateFilter(filterKey, next);
                    }}
                  />
                }
                excluded={excluded}
                required={required}
                onToggleExclude={() => {
                  const next = [...items];
                  next[i] = { ...item, mode: excluded ? "include" : "exclude" };
                  updateFilter(filterKey, next);
                }}
                onToggleRequired={() => {
                  const next = [...items];
                  next[i] = { ...item, required: !required };
                  updateFilter(filterKey, next);
                }}
                onRemove={() => updateFilter(filterKey, items.filter((_, j) => j !== i))}
              />
            );
          })}
          <ClearChipsButton onClick={() => updateFilter(filterKey, undefined)} />
        </div>
      )}
    </>
  );
}

/**
 * Whether the matching certification must be active. `any` is the schema
 * default, so it is written as "no value" rather than stored.
 */
function CertificationStatusField() {
  const { filters, updateFilter } = useTalentSearchFilters();
  const run = useOptionalTalentSearchResults()?.run;
  const id = useId();
  const status = filters.certification_status ?? "any";
  const help = CERTIFICATION_STATUS_OPTIONS.find((o) => o.value === status)?.help;
  // The latest run fell back to plain name/issuer matching: the profile
  // index predates per-certification expiry dates.
  const unavailable =
    status !== "any" && !!run?.degradedReasons.includes(CERTIFICATION_STATUS_UNAVAILABLE);

  return (
    <FilterFieldContainer>
      <FilterFieldLabel>Status</FilterFieldLabel>
      <FilterFieldContent className="flex flex-col gap-1.5">
        <RadioGroup
          aria-label="Certification status"
          value={status}
          onValueChange={(value) =>
            updateFilter(
              "certification_status",
              value === "any" ? undefined : (value as SearchFiltersSchemaCertificationStatus),
            )
          }
          className="flex flex-col gap-1.5"
        >
          {CERTIFICATION_STATUS_OPTIONS.map((o) => (
            <label
              key={o.value}
              htmlFor={`${id}-${o.value}`}
              className="flex cursor-pointer items-center gap-2 text-sm text-heading"
            >
              <RadioGroupItem id={`${id}-${o.value}`} value={o.value} />
              {o.label}
            </label>
          ))}
        </RadioGroup>
        {help ? <p className="text-xs text-contrast/50">{help}</p> : null}
        {unavailable ? (
          <p role="status" className="text-xs text-contrast/70">
            Not available until the profile index is rebuilt
          </p>
        ) : null}
      </FilterFieldContent>
    </FilterFieldContainer>
  );
}

export function FilterCertificationsSection() {
  return (
    <>
      <CertificationChipField filterKey="certification_names" label="Certification" />
      <CertificationChipField filterKey="certification_vendors" label="Issuing Organization" />
      <CertificationStatusField />
    </>
  );
}
